// src/pages/PrivacyPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

/* ---- Company meta (edit these) ---- */
const COMPANY = {
  name: "Razzle Dazzle Entertainment Pvt. Ltd.",
  brand: "ShowTimeBro",
  website: "https://showtimebro.com",
//   cin: "U92140MP2013PTC031550",
//   effectiveDate: "[Insert Date]",
  address: "210, Om Gurudev Plaza (Savitri Empire) Near Vikram Urban, Indore M.P. 452010, India",
  email: "support@smtlabs.io",
  phone: "9179981316",
};

function AccordionItem({ id, title, isOpen, onToggle, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-slate-800">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-slate-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        id={`panel-${id}`}
        role="region"
        className={`px-4 pb-5 text-sm leading-6 text-slate-700 ${
          isOpen ? "block" : "hidden"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function PrivacyPage() {
  const sections = useMemo(
    () => [
      {
        id: "intro",
        title: "🛡️ Privacy Policy – ShowTimeBro",
        body: (
          <>
            <div className="text-sm text-slate-600 space-y-1">
              {/* <div>
                <span className="font-medium">Effective Date:</span>{" "}
                {COMPANY.effectiveDate}
              </div> */}
              <div>
                <span className="font-medium">Company:</span> {COMPANY.name}
              </div>
              <div>
                <span className="font-medium">Website:</span>{" "}
                <a href={COMPANY.website} className="text-amber-600 underline">
                  {COMPANY.website}
                </a>
              </div>
              {/* <div>
                <span className="font-medium">CIN:</span> {COMPANY.cin}
              </div> */}
              <div>
                <span className="font-medium">Registered Office:</span>{" "}
                {COMPANY.address}
              </div>
            </div>

            <p className="mt-4">
              {COMPANY.name} (“Company”, “we”, “our”, or “us”) operates the
              website {COMPANY.brand} which enables users to discover, book, and
              participate in live events, shows, and entertainment experiences.
            </p>
            <p className="mt-2">
              We value your privacy and are committed to protecting your
              personal information in compliance with applicable Indian laws,
              including the Information Technology Act, 2000 and its
              Information Technology (Reasonable Security Practices and
              Procedures and Sensitive Personal Data or Information) Rules,
              2011.
            </p>
          </>
        ),
      },
      {
        id: "collect",
        title: "1) Information We Collect",
        body: (
          <>
            <p className="font-medium">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>
                <strong>Personal Information:</strong> Name, email address,
                phone number, date of birth, billing address, and payment
                details when booking or registering for events.
              </li>
              <li>
                <strong>Non-Personal Information:</strong> Browser type, IP
                address, cookies, device identifiers, and usage data to improve
                our website experience.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "use",
        title: "2) How We Use the Information",
        body: (
          <ul className="list-disc pl-6 space-y-1">
            <li>Process bookings and event registrations.</li>
            <li>Communicate about upcoming shows, updates, and offers.</li>
            <li>Personalize your experience on our platform.</li>
            <li>Maintain records for accounting, taxation, and legal compliance.</li>
            <li>Prevent fraud, unauthorized access, or misuse of our services.</li>
          </ul>
        ),
      },
      {
        id: "share",
        title: "3) Sharing and Disclosure",
        body: (
          <>
            <p>We do not sell or rent your information. However, we may share it with:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>
                Event organizers or partners for ticket validation and access.
              </li>
              <li>Payment gateways to process transactions securely.</li>
              <li>Law enforcement authorities when legally required.</li>
            </ul>
          </>
        ),
      },
      {
        id: "cookies",
        title: "4) Cookies",
        body: (
          <p>
            We use cookies and analytics tools to understand user behavior,
            improve site performance, and deliver targeted promotions. You can
            disable cookies in your browser settings.
          </p>
        ),
      },
      {
        id: "security",
        title: "5) Data Security",
        body: (
          <p>
            We follow industry-standard practices (encryption, firewalls,
            limited access) to secure your data. However, no online transmission
            is 100% secure, and users accept this inherent risk.
          </p>
        ),
      },
      {
        id: "retention",
        title: "6) Retention",
        body: (
          <p>
            Your personal information is retained only as long as necessary to
            fulfill the purposes mentioned or as required by law.
          </p>
        ),
      },
      {
        id: "rights",
        title: "7) Your Rights",
        body: (
          <>
            <p>You may:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Access, correct, or request deletion of your data.</li>
              <li>Withdraw consent for marketing communication.</li>
              <li>
                Contact us for privacy concerns at{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-amber-600 underline break-all"
                >
                  {COMPANY.email}
                </a>
                .
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "updates",
        title: "8) Updates",
        body: (
          <p>
            We may revise this Privacy Policy from time to time. Updated
            versions will be posted on this page with the revised “Effective
            Date”.
          </p>
        ),
      },
      {
        id: "terms",
        title: "⚖️ Terms of Use – ShowTimeBro",
        body: (
          <>
            {/* <div className="text-sm text-slate-600">
              <span className="font-medium">Effective Date:</span>{" "}
              {COMPANY.effectiveDate}
            </div> */}
            <ol className="list-decimal pl-6 space-y-2 mt-3">
              <li>
                <strong>Acceptance of Terms:</strong> By accessing or using
                ShowTimeBro, you agree to these Terms of Use, our Privacy
                Policy, and any applicable event-specific terms.
              </li>
              <li>
                <strong>Services:</strong> Platform to view, promote, and book
                tickets for entertainment events, concerts, and shows organized
                by {COMPANY.name} or its verified partners.
              </li>
              <li>
                <strong>User Responsibilities:</strong> Provide accurate
                information; do not misuse the website (no hacking, scraping, or
                spamming); comply with laws and event rules.
              </li>
              <li>
                <strong>Ticket booking & Payments:</strong> Payments via secure
                gateways; tickets may be non-refundable unless canceled or
                rescheduled; convenience fees are non-refundable; verify event
                details before purchase.
              </li>
              <li>
                <strong>Event Cancellations/Changes:</strong> We may cancel,
                postpone, or alter events; refunds/credits per organizer policy.
              </li>
              <li>
                <strong>Intellectual Property:</strong> All site content and
                logos belong to {COMPANY.name} or licensors; unauthorized use is
                prohibited.
              </li>
              <li>
                <strong>Limitation of Liability:</strong> Not liable for event
                cancellations/delays, third-party failures, or user misuse.
              </li>
              <li>
                <strong>Indemnity:</strong> You agree to indemnify and hold us
                harmless from claims arising out of your misuse.
              </li>
              <li>
                <strong>Governing Law:</strong> Laws of India; courts in
                Indore, Madhya Pradesh have jurisdiction.
              </li>
              <li>
                <strong>Contact:</strong> {COMPANY.name} •{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-amber-600 underline"
                >
                  {COMPANY.email}
                </a>{" "}
                • {COMPANY.phone} • {COMPANY.address}
              </li>
            </ol>
            {/* <p className="mt-3">
              For the full Terms page with accordions, visit{" "}
              <a href="/terms" className="text-amber-600 underline">
                /terms
              </a>
              .
            </p> */}
          </>
        ),
      },
    ],
    []
  );

  const [openId, setOpenId] = useState(null);

  // open a section by hash (#cookies etc.)
  useEffect(() => {
    const h = window.location.hash?.replace("#", "");
    if (h) setOpenId(h);
  }, []);

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h1 className="text-2xl font-semibold text-slate-900">Privacy Policy</h1>
          {/* <p className="mt-1 text-sm text-slate-600">
            Effective Date: {COMPANY.effectiveDate}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Company: {COMPANY.name} • CIN: {COMPANY.cin}
          </p> */}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setOpenId("__all__")}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Expand all
          </button>
          <button
            onClick={() => setOpenId(null)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Collapse all
          </button>
        </div>

        <div className="space-y-3">
          {sections.map((s) => {
            const isOpen = openId === "__all__" || openId === s.id;
            return (
              <AccordionItem
                key={s.id}
                id={s.id}
                title={s.title}
                isOpen={isOpen}
                onToggle={() => setOpenId(isOpen ? null : s.id)}
              >
                {s.body}
              </AccordionItem>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-slate-500">
          Note: Replace placeholders like <em>[Insert Date]</em>,{" "}
          <em>[Insert number]</em>, and address before publishing.
        </p>
      </div>
    </main>
  );
}
